import { useMemo } from 'react';
import { useDebouncedValue } from '@reflex/ui/hooks/useDebouncedValue';
import { useAgents, useAgentSearch } from '@/hooks/agents/useAgents';
import { useAgentViews } from '@/hooks/agents/useAgentViews';
import { mergeAgentsById } from '@/lib/agent-utils';
import {
  rankSessionJumpEntries,
  type SessionJumpEntry,
  type SessionJumpMode,
} from '@/lib/session-jump';

/** Rows the palette shows at once — a keyboard list, not a browsable one. */
export const SESSION_JUMP_LIMIT = 25;
/** Below this the server search is all noise; local rows already cover it. */
const MIN_SERVER_SEARCH_LENGTH = 2;
/** One request per typed word rather than one per keystroke. */
const SEARCH_DEBOUNCE_MS = 250;
/** Server-side hits to graft on, matching the sidebar's search budget. */
const SEARCH_RESULT_LIMIT = 50;

export interface UseSessionJumpEntriesResult {
  entries: SessionJumpEntry[];
  /** True while the first page of runs is still loading. */
  isLoading: boolean;
}

/**
 * The ranked rows behind the jump palette.
 *
 * Sessions come from the same paginated list the sidebar reads (so the cache,
 * and its live WebSocket patches, are shared rather than duplicated), with the
 * server-side search grafted on for runs that sit past the loaded pages. The
 * viewer's last-viewed map supplies recency for both the ordering and the
 * `recent` mode's membership.
 *
 * Deliberately unscoped by owner: the palette is a global "jump to anything I
 * can see", not a view of the sidebar's current owner filter.
 *
 * Mount this only while the palette is open — it is what keeps the always-
 * mounted palette from holding an agents query open on every page.
 */
export function useSessionJumpEntries(
  query: string,
  mode: SessionJumpMode,
): UseSessionJumpEntriesResult {
  // No filters: the palette is a global "jump to anything I can see", so it
  // shares the sidebar's unfiltered agent list rather than any owner scope.
  const { data: loadedAgents, isLoading } = useAgents();
  const views = useAgentViews();
  const trimmed = query.trim();
  const debouncedQuery = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  // Runs the loaded pages have not reached — including ones only findable by
  // their prompt text. `recent` needs this too: a run visited long enough ago
  // to have fallen off the loaded pages is exactly what a history should find.
  const { data: searchedAgents } = useAgentSearch(debouncedQuery, SEARCH_RESULT_LIMIT, {
    enabled: debouncedQuery.length >= MIN_SERVER_SEARCH_LENGTH,
  });

  const agents = useMemo(
    () => mergeAgentsById(loadedAgents ?? [], searchedAgents),
    [loadedAgents, searchedAgents],
  );

  const entries = useMemo(
    () =>
      rankSessionJumpEntries({ agents, views, query: trimmed, mode, limit: SESSION_JUMP_LIMIT }),
    [agents, views, trimmed, mode],
  );

  return { entries, isLoading };
}
